import type { ExportOptions, OrderOptions } from '@bkbnlab/api'
import { AiJobSceneDTO } from 'models/photoStudio'
import { LabDetectedSource, toLabEngineRequest } from './labEngine'
import { LabLocalStage, LabProgress, LabSceneContent, LabStageClock, resolveLabPrivacyCategory } from './labSdk.types'
import { LabOrderStage, nextLabStageClock, parseLabOrderStages } from './labStageClock'
import { createImageBlobUrl, revokeBlobUrl } from 'utils/image'
import { useCallback, useEffect, useRef } from 'react'

import { LabEngine } from './useLabEngine.hook'
import { logger } from 'utils/logging'
import { preloadImage } from './preloadImage'

interface UseLabGenerationProps {
  engine: LabEngine
  /** The image the order runs on as a File — the current preview, or `sourceImageUrl` when the run starts elsewhere. */
  getSourceFile: (sourceImageUrl?: string) => Promise<File>
  /** Uploads to the platform's temp bucket (save-temporary-visual); resolves with the stored filename */
  uploadVisual: (file: File) => Promise<string>
  /** Uploads an eraser mask (mask-upload-url); resolves with the stored filename */
  uploadMask: (file: File) => Promise<string>
  onProgress: (progress: LabProgress | null) => void
  /** The steps of the order as they finish (the socket's scenes), to stage over the photo */
  onScene?: (scene: LabSceneContent) => void
  /** Persist the image before exposing success or enabling save. Returns the history URL. */
  commitResult: (resultUrl: string, meta: { jobId: string; signal: AbortSignal }) => Promise<string>
  onComplete: (resultUrl: string, meta: { jobId: string }) => void | Promise<void>
  onError: (error: unknown) => void
}

interface RunLabOrderParams {
  /** Builds the order in Lab SDK terms (the panels' mapping) — the asset ids it is given are the platform's filenames */
  buildOrder: (inputAssetId: string, maskAssetId?: string) => OrderOptions
  exportOptions: ExportOptions
  /** Magic eraser only: white where to erase on a transparent background (`labMask.ts`) */
  maskFile?: File
  /** Snapshot the current version and return the selected input image. Failures use the run error handler. */
  prepareSource: (signal: AbortSignal) => Promise<string>
  /** Eraser only: the upload and object detection of the input image, once that detection FINISHED */
  detectedSource?: (sourceImageUrl: string) => LabDetectedSource | undefined
  /** For logging */
  action: string
}

/** A socket scene as the preview stages it; null when it lacks what its kind needs */
export const toLabSceneContent = (scene: AiJobSceneDTO): LabSceneContent | null => {
  if (scene.kind === 'DETECT') {
    const category = resolveLabPrivacyCategory(scene.label)
    return scene.mask ? { kind: 'detect', categories: category ? [category] : [], coverage: scene.coverage, mask: scene.mask } : null
  }
  if (!scene.image) return null
  return { kind: scene.kind === 'EDIT' ? 'edit' : 'generate', image: scene.image, steps: scene.steps ?? [] }
}

/** Upper bound on a run — the slowest speed tier is estimated at ~90s, so this leaves room for a queue. */
export const LAB_ORDER_TIMEOUT_MS = 10 * 60 * 1000

export interface LabGenerationResult {
  success: boolean
  resultUrl?: string
  jobId?: string
  error?: unknown
}

/**
 * The Lab generation flow shared by Enhance, Stage and Erase, run by the platform:
 * upload(source[, mask]) to the temp bucket → start the Lab tool → follow its job → download the result → history →
 * onComplete(url).
 *
 * The job is followed over the platform's socket with polling alongside; progress arrives as the Lab's real stage
 * names and percentages with its stage plan, which the preview overlay renders instead of the canned messages.
 */
export const useLabGeneration = ({ engine, getSourceFile, uploadVisual, uploadMask, commitResult, onProgress, onScene, onComplete, onError }: UseLabGenerationProps) => {
  // Refs keep the callbacks fresh without re-creating `runLabOrder` on every render of the controller —
  // a run reads `callbacksRef.current` at each step, never a copy taken when it started.
  const callbacksRef = useRef({ engine, getSourceFile, uploadVisual, uploadMask, commitResult, onProgress, onScene, onComplete, onError })
  callbacksRef.current = { engine, getSourceFile, uploadVisual, uploadMask, commitResult, onProgress, onScene, onComplete, onError }

  const lifetimeRef = useRef(new AbortController())
  useEffect(() => {
    const lifetime = new AbortController()
    lifetimeRef.current = lifetime
    return () => lifetime.abort()
  }, [])

  const runLabOrder = useCallback(async ({ buildOrder, exportOptions, maskFile, prepareSource, detectedSource, action }: RunLabOrderParams): Promise<LabGenerationResult> => {
    let jobId: string | undefined
    let pendingBlobUrl: string | undefined
    const signal = lifetimeRef.current.signal
    const releasePendingBlob = () => {
      if (pendingBlobUrl) revokeBlobUrl(pendingBlobUrl)
      pendingBlobUrl = undefined
    }
    signal.addEventListener('abort', releasePendingBlob, { once: true })
    try {
      signal.throwIfAborted()

      const sourceImageUrl = await prepareSource(signal)
      signal.throwIfAborted()
      // Each step before the job runs is named (the overlay's "Prepare" segment), so the seconds an upload takes never
      // read as an unexplained "Generating… 0%".
      // Already uploaded for an object detection that finished? Reuse both; otherwise (still running, failed) upload afresh
      const detected = detectedSource?.(sourceImageUrl)
      let visualFilename = detected?.visualFilename
      if (!visualFilename) {
        callbacksRef.current.onProgress({ stage: LabLocalStage.UPLOAD, percent: 0 })
        const sourceFile = await callbacksRef.current.getSourceFile(sourceImageUrl)
        signal.throwIfAborted()
        visualFilename = await callbacksRef.current.uploadVisual(sourceFile)
        signal.throwIfAborted()
      }
      if (maskFile) callbacksRef.current.onProgress({ stage: LabLocalStage.UPLOAD_MASK, percent: 0 })
      const maskFilename = maskFile ? await callbacksRef.current.uploadMask(maskFile) : undefined
      signal.throwIfAborted()

      callbacksRef.current.onProgress({ stage: LabLocalStage.QUEUED, percent: 3 })
      const request = toLabEngineRequest(buildOrder(visualFilename, maskFilename), exportOptions, {
        visualFilename,
        maskFilename,
        objectsJobId: detected?.objectsJobId,
      })
      jobId = await callbacksRef.current.engine.startJob(request)
      signal.throwIfAborted()

      // The Lab's stage plan comes with the progress: read once, it lets the overlay advance between real events — a
      // cloud generation holds the same percentage until it is done. Without it the overlay follows the events only.
      let stages: LabOrderStage[] = []
      let clock: LabStageClock | undefined
      await callbacksRef.current.engine.waitForJob(jobId, {
        signal,
        timeoutMs: LAB_ORDER_TIMEOUT_MS,
        onProgress: (progress) => {
          if (signal.aborted) return
          if (stages.length === 0) stages = parseLabOrderStages(progress.stages)
          if (!progress.stage) return
          const percent = progress.percent ?? 0
          clock = nextLabStageClock(stages, clock, { stage: progress.stage, percent }, Date.now())
          callbacksRef.current.onProgress({ stage: progress.stage, percent, etaMs: progress.etaMs ?? undefined, clock })
        },
        onScene: (scene) => {
          const content = toLabSceneContent(scene)
          if (content && !signal.aborted) callbacksRef.current.onScene?.(content)
        },
      })
      signal.throwIfAborted()

      callbacksRef.current.onProgress({ stage: LabLocalStage.EXPORT, percent: 99 })
      const response = await callbacksRef.current.engine.fetchResult(jobId, signal)
      signal.throwIfAborted()
      const blob = await response.blob()
      signal.throwIfAborted()
      // The bucket names the type; a generic one falls back to the format the run asked for
      const image = blob.type.startsWith('image/') ? blob : new Blob([blob], { type: `image/${exportOptions.format}` })
      pendingBlobUrl = createImageBlobUrl(image)
      await preloadImage(pendingBlobUrl)
      signal.throwIfAborted()

      const completion = callbacksRef.current
      const resultUrl = await completion.commitResult(pendingBlobUrl, { jobId, signal })
      signal.throwIfAborted()
      pendingBlobUrl = undefined // History now owns the image and its cleanup.
      completion.onProgress(null)
      await completion.onComplete(resultUrl, { jobId })
      return { success: true, resultUrl, jobId }
    } catch (error) {
      if (signal.aborted) return { success: false, error }
      logger.error('Lab engine: generation failed', error, {
        context: {
          component: 'PhotoStudio',
          action,
          metadata: { engine: 'lab-platform', jobId },
        },
      })
      callbacksRef.current.onProgress(null)
      callbacksRef.current.onError(error)
      return { success: false, error }
    } finally {
      signal.removeEventListener('abort', releasePendingBlob)
      releasePendingBlob()
    }
  }, [])

  return { runLabOrder }
}
